import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createOrder } from "../services/api";
import { useNotification } from "../context/NotificationContext";

const Order = () => {
  const navigate = useNavigate();
  const { cart, shippingAddress, paymentMethod, shippingMethod, dispatch } = useCart();
  const { showNotification } = useNotification();

  const handleShippingPage = () => {
    navigate("/shippig_payment");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missingFields = [];
    if (cart.length === 0) missingFields.push("panier");
    if (!shippingAddress) missingFields.push("adresse de livraison");
    if (!shippingMethod) missingFields.push("méthode de livraison");
    if (!paymentMethod) missingFields.push("méthode de paiement");

    if (missingFields.length > 0) {
      showNotification(`Informations manquantes pour la commande : ${missingFields.join(", ")}`, 'error');
      return;
    }

    const requiredAddressFields = ["street", "postalCode", "city", "country"];
    const missingAddressDetails = requiredAddressFields.filter((field) => !shippingAddress[field]);

    if (missingAddressDetails.length > 0) {
      showNotification(`Adresse de livraison incomplète : ${missingAddressDetails.join(", ")}`, 'error');
      return;
    }

    try {
      const orderDetails = {
        items: cart.map((item) => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
        total: cart.reduce((acc, item) => acc + item.price * item.quantity, 0),
        shippingAddress,
        paymentMethod,
        shippingMethod,
      };

      const response = await createOrder(orderDetails);

      if (response.data?.error || response.error) {
        showNotification(`Erreur : ${response.data?.message || response.message || "Échec de la commande"}`, 'error');
        return;
      }

      dispatch({ type: "CLEAR_CART" });
      showNotification("Commande confirmée avec succès !", 'success');
      navigate("/");
    } catch (error) {
      console.error("Erreur lors de la commande", error);
      showNotification("Une erreur technique est survenue. Veuillez réessayer.", 'error');
    }
  };

  const orderTotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Synthèse de commande</h2>
        <p className="text-slate-500 text-sm mt-1">Veuillez vérifier les informations ci-dessous avant de valider.</p>
      </div>

      {cart.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500 text-sm">Votre panier est vide.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Produits commandés */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-655">Produits dans la commande</h3>
            </div>
            
            <div className="divide-y divide-slate-100 px-6">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between items-center py-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-slate-800">{item.name}</h4>
                    <span className="text-xs text-slate-400">Quantité : {item.quantity}</span>
                  </div>
                  <div className="text-right font-medium text-slate-800">
                    {(item.price * item.quantity).toFixed(2)} €
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex justify-between items-center font-bold text-base text-slate-800">
              <span>Total</span>
              <span>{orderTotal.toFixed(2)} €</span>
            </div>
          </div>

          {/* Adresses et options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Adresse */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-450 mb-3">Adresse de livraison</h3>
                <div className="text-sm text-slate-700 bg-slate-50 p-4 rounded-lg space-y-1">
                  <p className="font-medium text-slate-800">{shippingAddress.street}</p>
                  <p>{shippingAddress.postalCode} {shippingAddress.city}</p>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{shippingAddress.country}</p>
                </div>
              </div>
              <button
                onClick={handleShippingPage}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 mt-4 text-left inline-flex items-center gap-1"
              >
                Modifier l'adresse
              </button>
            </div>

            {/* Méthodes */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between">
              <div className="space-y-4">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-455 mb-1.5">Mode de livraison</h3>
                  <div className="text-sm text-slate-700 bg-slate-50 px-4 py-2 rounded-lg capitalize font-medium">
                    {shippingMethod || "Non spécifiée"}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-455 mb-1.5">Mode de paiement</h3>
                  <div className="text-sm text-slate-700 bg-slate-50 px-4 py-2 rounded-lg font-medium">
                    {paymentMethod || "Non spécifié"}
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleShippingPage}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 mt-4 text-left inline-flex items-center gap-1"
              >
                Modifier les options
              </button>
            </div>
          </div>

          {/* Action validation */}
          <div className="flex justify-end gap-4 pt-4 border-t border-slate-200">
            <button
              onClick={() => navigate('/shippig_payment')}
              className="px-6 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 text-sm font-medium transition-colors"
            >
              Retour
            </button>
            <button
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium text-sm transition-colors shadow-sm shadow-indigo-600/10"
            >
              Confirmer la commande
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Order;
